/**
 * A stored theme as the complete set of CSS variables theme.css reads. DOM-free, so the
 * storefront (whole page), the dashboard's preview (one container) and Storybook share it.
 *
 * Sanitizing stays in `@loot-os/api-contract`: the shadcn half goes through
 * `resolveStorefrontThemeVars` and the design-system half through `resolveThemeSpec`, both of
 * which loop over our keys and drop anything unsafe. Every value written here is either one of
 * theirs or one of ours (a preset or a `var()` reference), never a raw stored string.
 *
 * Always complete: every variable this module knows is written, so a theme applied to a box
 * inside another theme never half-inherits the outer one.
 */
import {
  formatHex,
  inferColorScheme,
  parseHex,
  type ResolvedThemeSpec,
  readableTextColor,
  resolveRarityColor,
  resolveStorefrontThemeVars,
  resolveStorefrontTokens,
  resolveThemeSpec,
  SEMANTIC_COLOR_KEYS,
  type StorefrontFontId,
  type StorefrontTheme,
  safeThemeFontFamily,
  toLoadableFontStack,
} from "@loot-os/api-contract";

import {
  BASE_RADIUS_PX,
  CHASE_DEFAULT,
  CONTROL_RADIUS,
  DENSITY_SCALE,
  DISPLAY_TRACKING_SCALE,
  GLASS_BLUR_PX,
  GLOW_ALPHA,
  MOTION,
  STATUS_DEFAULTS,
} from "./presets.js";

/** The rarity tiers the contract names, in order (`rarity/common` … `rarity/legendary`). */
export const RARITY_ROLES = ["common", "uncommon", "rare", "epic", "legendary"] as const;

export interface ThemeVars {
  /** Custom properties, keyed with their `--`. Always the complete set. */
  properties: Record<string, string>;
  /** Set as the element's `font-family`: a property, not a variable. */
  fontFamily: string;
  /** The catalog fonts the theme's three stacks need loaded, each once. */
  fonts: StorefrontFontId[];
  /** `dark` or `light` from the canvas color; null when it can't be judged. */
  colorScheme: "dark" | "light" | null;
  /** The resolved choices, for React context (layout, stage recipe, motion). */
  spec: ResolvedThemeSpec;
}

const SYSTEM_SANS = "ui-sans-serif, system-ui, sans-serif";
const SYSTEM_MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

function fontStack(value: string | undefined) {
  const safe = safeThemeFontFamily(value);
  return safe ? toLoadableFontStack(safe) : null;
}

/** Each text weight as the strong body roles' `font-weight`. */
const TEXT_WEIGHT = { light: 500, medium: 600, heavy: 700 } as const;

/** How much of its tier a surface takes under a tier's word at most (an odds chip's `/5`). */
const TIER_WASH = 0.06;

/**
 * `ground` under `tint` at `amount` opacity, as the browser composites a translucent fill (in
 * sRGB); null when either isn't a hex.
 */
function tintHex(ground: string, tint: string, amount: number): string | null {
  const below = parseHex(ground);
  const above = parseHex(tint);
  if (!below || !above) return null;
  const blend = (from: number, to: number) => from + (to - from) * amount;
  return formatHex([
    blend(below[0], above[0]),
    blend(below[1], above[1]),
    blend(below[2], above[2]),
  ]);
}

/** A sanitized radius as a multiple of the base radius; 1 when it isn't in px or rem. */
export function radiusScale(radius: string | null): number {
  const match = /^(\d+(?:\.\d+)?)(px|rem)$/.exec(radius ?? "");
  if (!match) return 1;
  const px = Number(match[1]) * (match[2] === "rem" ? 16 : 1);
  return Math.round((px / BASE_RADIUS_PX) * 1000) / 1000;
}

/** Every CSS variable a stored theme sets, sanitized and complete. */
export function resolveThemeVars(theme?: StorefrontTheme | null): ThemeVars {
  const base = resolveStorefrontThemeVars(theme ?? undefined);
  const spec = resolveThemeSpec(theme);
  const properties: Record<string, string> = { ...base.colors };

  // Semantic roles: a set role is written; an unset one is cleared to "initial" so theme.css's
  // derivation applies instead of an outer theme's value.
  for (const key of SEMANTIC_COLOR_KEYS) {
    properties[`--theme-${key}`] = spec.semantic[key] ?? "initial";
  }
  for (const [key, value] of Object.entries(STATUS_DEFAULTS)) {
    properties[`--theme-${key}`] = spec.semantic[key as keyof typeof STATUS_DEFAULTS] ?? value;
  }
  properties["--theme-rarity-chase"] = spec.semantic["rarity-chase"] ?? CHASE_DEFAULT;
  RARITY_ROLES.forEach((role, tier) => {
    properties[`--theme-rarity-${role}`] = resolveRarityColor(tier, theme?.rarity).color;
  });
  // Each tier's color as words take it: the tier itself where it reads 4.5:1 on the canvas and on a
  // card (a store that tuned its tiers keeps them exactly), else mixed toward the text just far
  // enough. A tier that isn't a hex (a `var()`) is its own text color.
  const tokens = resolveStorefrontTokens(theme?.tokens);
  for (const role of [...RARITY_ROLES, "chase"] as const) {
    const fill = properties[`--theme-rarity-${role}`] ?? "";
    // The grounds a tier's word sits on: the canvas, a card, and either one under a light wash of
    // the tier itself (an odds chip's).
    const grounds = [tokens.background, tokens.card].flatMap((ground) => [
      ground,
      tintHex(ground, fill, TIER_WASH),
    ]);
    properties[`--theme-rarity-text-${role}`] =
      (grounds.every((ground) => ground !== null) &&
        readableTextColor(fill, grounds as string[], tokens.foreground)) ||
      `var(--theme-rarity-${role})`;
  }

  // Glass off: glass roles render as solid raised surfaces, so no component branches on it.
  if (spec.surface.glass === 0) {
    properties["--theme-glass-fill"] = "var(--color-bg-raised)";
    properties["--theme-glass-edge"] = "var(--color-border-subtle)";
  }
  properties["--theme-glass-blur"] = `${GLASS_BLUR_PX[spec.surface.glass]}px`;
  properties["--theme-glow-alpha"] = GLOW_ALPHA[spec.glow.intensity];

  const soft = spec.surface.style === "soft";
  properties["--theme-surface-border"] = soft ? "transparent" : "var(--color-border-subtle)";
  properties["--theme-surface-shadow"] = soft
    ? "inset 0 1px 0 0 color-mix(in oklab, var(--color-text-primary) 7%, transparent)"
    : "none";

  properties["--theme-radius-scale"] = String(radiusScale(base.radius));
  properties["--theme-radius-control"] = CONTROL_RADIUS[spec.shape.controls];
  properties["--theme-density"] = String(DENSITY_SCALE[spec.shape.density]);
  properties["--theme-page-margin"] = `${spec.layout.pageMargin}px`;
  // Outlined: a select is a sunken well with the 3:1 input edge, a secondary button raised with an
  // edge. Filled: both take the secondary color, no edge, and lighten a step under the pointer (one
  // that keeps muted labels at 4.5:1); a text
  // field keeps its well with the quiet default edge.
  const filled = spec.shape.controlStyle === "filled";
  const filledHover = "color-mix(in srgb, var(--secondary) 96%, var(--foreground))";
  properties["--theme-field-edge"] = filled
    ? "var(--color-border-default)"
    : "var(--color-border-input)";
  properties["--theme-select-fill"] = filled ? "var(--secondary)" : "var(--color-bg-inset)";
  properties["--theme-select-edge"] = filled ? "transparent" : "var(--color-field-edge)";
  properties["--theme-control-hover"] = filled ? filledHover : "var(--color-bg-hover)";
  properties["--theme-secondary-fill"] = filled ? "var(--secondary)" : "var(--color-bg-raised)";
  properties["--theme-secondary-edge"] = filled ? "transparent" : "var(--color-border-default)";

  properties["--theme-display-weight"] = String(spec.type.displayWeight);
  properties["--theme-display-tracking"] = String(
    DISPLAY_TRACKING_SCALE[spec.type.displayTracking],
  );
  properties["--theme-display-case"] = spec.type.displayCase === "upper" ? "uppercase" : "none";
  properties["--theme-label-case"] = spec.type.labelCase === "upper" ? "uppercase" : "none";
  properties["--theme-strong-weight"] = String(TEXT_WEIGHT[spec.type.textWeight]);
  properties["--theme-amount-font"] =
    spec.type.amountFont === "body" ? "var(--font-body)" : "var(--font-mono)";

  const motion = MOTION[spec.motion.preset];
  for (const [name, ms] of Object.entries(motion.durations)) {
    properties[`--theme-duration-${name}`] = `${ms}ms`;
  }
  for (const [name, curve] of Object.entries(motion.easings)) {
    properties[`--theme-ease-${name}`] = curve;
  }

  for (let i = 0; i < 4; i++) {
    properties[`--theme-stage-${i + 1}`] = spec.stage.colors[i] ?? "transparent";
  }

  const body = base.fontFamily ?? SYSTEM_SANS;
  const mono = fontStack(spec.monoFontFamily);
  properties["--theme-font-body"] = body;
  properties["--theme-font-display"] = base.headingFontFamily ?? body;
  properties["--theme-font-mono"] = mono?.stack ?? SYSTEM_MONO;

  const fonts = new Set<StorefrontFontId>(base.fonts);
  if (mono?.font) fonts.add(mono.font.id);

  return {
    properties,
    fontFamily: body,
    fonts: [...fonts],
    colorScheme: inferColorScheme(base.colors["--background"]),
    spec,
  };
}
